// @flow

/**
 * Event Timeline Component
 * Displays a grid of processing events with durations and status indicators.
 * Supports multiple concurrent active events (parallel tool calls).
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import TypingIndicator from './TypingIndicator';
import { formatDuration } from '../utils/formatters';

const StatusList = styled.div`
  display: inline-grid;
  /* Lock column widths so timings don't shift when bubble width changes */
  grid-template-columns: 30ch 8ch;
  column-gap: 0.4rem;
  row-gap: 0.25rem;
  align-items: baseline;
`;

const StatusIcon = styled.span`
  font-size: 0.9rem;
  line-height: 1;
  color: ${(p) => (p.$error ? 'var(--red, #d32f2f)' : 'var(--blue, #1976d2)')};
`;

const DurationText = styled.span`
  opacity: 0.7;
  font-size: 0.85em;
  line-height: 1.2;
`;

const LeftCell = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  min-height: 1.2em;
`;

const RightCell = styled.div`
  align-self: baseline;
`;

const EventTimeline = ({ events, isActive, hasText }) => {
  const [, setTick] = useState(0);

  const hasActiveEvents = isActive && events && events.some(ev => ev.withDots);

  // Tick every second while any event is actively animating (for live duration counter)
  useEffect(() => {
    if (!hasActiveEvents) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [hasActiveEvents]);

  if (!events || events.length === 0) return null;

  return (
    <StatusList>
      {events.map((ev) => {
        const animate = isActive && ev.withDots && !hasText;
        const colorStyle = ev.color ? { color: ev.color } : undefined;

        const left = animate ? (
          <TypingIndicator aria-live="polite">
            <i style={colorStyle}>{ev.text || ''}</i>
            <span />
            <span />
            <span />
          </TypingIndicator>
        ) : (
          <>
            <StatusIcon
              $error={Boolean(ev.error)}
              aria-label={ev.error ? 'Failed' : 'Completed'}
              title={ev.error ? 'Failed' : 'Completed'}
            >
              {ev.error ? '✘' : '✔'}
            </StatusIcon>
            <span style={colorStyle}>{ev.text || ''}</span>
          </>
        );

        return (
          <React.Fragment key={ev.id}>
            <LeftCell>{left}</LeftCell>
            <RightCell>
              {animate && typeof ev.startAt === 'number' ? (
                <DurationText>{formatDuration(Date.now() - ev.startAt)}</DurationText>
              ) : !animate && typeof ev.durationMs === 'number' ? (
                <DurationText>{formatDuration(ev.durationMs)}</DurationText>
              ) : null}
            </RightCell>
          </React.Fragment>
        );
      })}
    </StatusList>
  );
};

export default EventTimeline;
